import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { Skeleton } from '@/shared/ui/Skeleton'
import { AlertModal } from '@/shared/ui/AlertModal'
import AffiliatesTable from '../components/AffiliatesTable'
import CreateAffiliateModal from '../components/CreateAffiliateModal.clean'
import EditAffiliateModal from '../components/EditAffiliateModal.clean'
import { useAffiliates } from '../hooks/useAffiliates'
import AffiliateInvitesPanel from '../components/AffiliateInvitesPanel'
import AffiliateLeadsPanel from '../components/AffiliateLeadsPanel'
import { Tabs } from '@/shared/ui/Tabs'

export default function AffiliatesPage() {
  const { user } = useAuth()
  const { items, total, page, setPage, pageSize, search, setSearch, loading, error, load, suspendAffiliate, activateAffiliate, deleteAffiliate } = useAffiliates()
  const [localError, setLocalError] = React.useState<string | null>(null)
  const [openCreate, setOpenCreate] = React.useState(false)
  const [openEdit, setOpenEdit] = React.useState(false)
  const [current, setCurrent] = React.useState<any|null>(null)
  const [confirmSuspendOpen, setConfirmSuspendOpen] = React.useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false)
  const [pendingAction, setPendingAction] = React.useState<'suspend'|'activate'|'delete'|null>(null)

  React.useEffect(() => { load() }, [load])

  function onCreate() { setOpenCreate(true) }
  function onEdit(a: any) {
    setCurrent(a)
    setOpenEdit(true)
  }
  function onToggleActive(a: any) {
    setCurrent(a)
    setPendingAction(a.status === 'ativo' ? 'suspend' : 'activate')
    setConfirmSuspendOpen(true)
  }
  function onDelete(a?: any) {
    if (a) setCurrent(a)
    setPendingAction('delete')
    setConfirmDeleteOpen(true)
  }

  async function handleConfirmSuspend() {
    if (!current) return
    try {
      if (pendingAction === 'suspend') await suspendAffiliate(current.id)
      if (pendingAction === 'activate') await activateAffiliate(current.id)
      await load()
    } catch (e:any) {
      setLocalError(e?.message ?? 'Falha ao atualizar status do afiliado')
    } finally {
      setConfirmSuspendOpen(false)
      setPendingAction(null)
    }
  }

  async function handleConfirmDelete() {
    if (!current) return
    try {
      await deleteAffiliate(current.id)
      await load()
    } catch (e:any) {
      setLocalError(e?.message ?? 'Falha ao excluir afiliado')
    } finally {
      setConfirmDeleteOpen(false)
      setPendingAction(null)
    }
  }

  const [tab, setTab] = React.useState<'list'|'invites'|'leads'>('list')

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <CreateAffiliateModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={() => load()} />
      <EditAffiliateModal open={openEdit} affiliate={current} onClose={() => setOpenEdit(false)} onUpdated={() => load()} />
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Afiliados</h1>
        <p className="text-sm text-foreground/70">Gerencie os afiliados cadastrados no sistema.</p>
      </div>

      {/* Tabs: Listagem (ativa por padrão) e Convites */}
      <Tabs
        tabs={[
          { value: 'list', label: 'Afiliados' },
          { value: 'invites', label: 'Convites' },
          { value: 'leads', label: 'Interessados' },
        ]}
        value={tab}
        onValueChange={(v)=> setTab(v as 'list'|'invites'|'leads')}
        className="mb-6"
      />

      {tab === 'invites' && (
        <div className="mb-8">
          <AffiliateInvitesPanel />
        </div>
      )}

      {tab === 'leads' && (
        <div className="mb-8">
          <AffiliateLeadsPanel />
        </div>
      )}

      {(error || localError) && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error || localError}</div>}

      {tab === 'list' && (loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <AffiliatesTable
          items={items}
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          search={search}
          onSearchChange={(v) => { setPage(1); setSearch(v) }}
          onCreate={onCreate}
          onEdit={onEdit as any}
          onDelete={onDelete as any}
          onToggleActive={onToggleActive as any}
        />
      ))}

      {/* Confirm Suspend/Activate */}
      <AlertModal
        open={confirmSuspendOpen}
        onClose={() => setConfirmSuspendOpen(false)}
        title={pendingAction === 'suspend' ? 'Suspender afiliado' : 'Ativar afiliado'}
        variant={pendingAction === 'suspend' ? 'warning' : 'info'}
        description={
          pendingAction === 'suspend'
            ? 'Ao suspender, este afiliado não poderá acessar o sistema até ser reativado.'
            : 'O afiliado voltará a ter acesso ao sistema.'
        }
        confirmText={pendingAction === 'suspend' ? 'Suspender' : 'Ativar'}
        onConfirm={handleConfirmSuspend}
      />

      {/* Confirm Delete */}
      <AlertModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Excluir afiliado"
        variant="error"
        description={'Esta ação é irreversível. O usuário, seu perfil e os registros de afiliado serão removidos permanentemente.'}
        confirmText="Excluir definitivamente"
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
